'use client'

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { 
  FaRegSadTear, 
  FaRegSmile, 
  FaRegFrown, 
  FaInfoCircle, 
  FaQuestionCircle,
  FaBook,
  FaLightbulb,
  FaLeaf
} from 'react-icons/fa';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const FoodServingSizeCalculator = () => {
  const [inputs, setInputs] = useState({
    foodItem: '',
    servings: '',
    weight: '',
    unit: 'grams'
  });
  const [results, setResults] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateServingSize = () => {
    if (!inputs.foodItem || !inputs.servings || !inputs.weight) return;

    const weightPerServing = parseFloat(inputs.weight) / parseFloat(inputs.servings);
    const totalWeight = parseFloat(inputs.weight);

    setResults({
      foodItem: inputs.foodItem,
      servings: inputs.servings,
      weightPerServing: weightPerServing.toFixed(2),
      totalWeight: totalWeight.toFixed(2),
      unit: inputs.unit
    });
  };

  const pieData = {
    labels: ['Per Serving', 'Remaining Food'],
    datasets: [
      {
        data: results ? [
          parseFloat(results.weightPerServing),
          parseFloat(results.totalWeight) - parseFloat(results.weightPerServing)
        ] : [0, 0],
        backgroundColor: [
          'rgb(75, 192, 192)',
          'rgb(255, 99, 132)'
        ],
        hoverOffset: 4
      }
    ]
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Serving Size Distribution'
      }
    }
  };

  return (
    <div>
      <Helmet>
        <title>Food Serving Size Calculator - Calculate Proper Portion Sizes</title>
        <meta name="description" content="Calculate proper serving sizes for different foods. Get accurate portion control measurements for healthy eating." />
        <meta property="og:title" content="Food Serving Size Calculator" />
        <meta property="og:description" content="Calculate proper serving sizes for different foods. Get accurate portion control measurements for healthy eating." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/images/food-serving-calculator.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-green-600 mb-2">
            🍽️ Food Serving Size Calculator
          </h1>
          <p className="text-gray-600">
            Calculate proper serving sizes for different foods
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Food Item
            </label>
            <input
              type="text"
              name="foodItem"
              value={inputs.foodItem}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter food item name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Servings
            </label>
            <input
              type="number"
              name="servings"
              value={inputs.servings}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter number of servings"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Weight
            </label>
            <input
              type="number"
              name="weight"
              value={inputs.weight}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter total weight"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit
            </label>
            <select
              name="unit"
              value={inputs.unit}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="grams">Grams</option>
              <option value="ounces">Ounces</option>
              <option value="milliliters">Milliliters</option>
              <option value="cups">Cups</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculateServingSize}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors mb-8"
          disabled={!inputs.foodItem || !inputs.servings || !inputs.weight}
        >
          Calculate Serving Size
        </button>

        {results && (
          <div className="p-6 bg-green-50 rounded-lg border border-green-200 mb-8">
            <h2 className="text-xl font-semibold mb-4">Serving Size Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium">Food Item</h3>
                <p className="text-green-600 font-semibold">{results.foodItem}</p>
              </div>
              <div>
                <h3 className="font-medium">Number of Servings</h3>
                <p className="text-green-600 font-semibold">{results.servings}</p>
              </div>
              <div>
                <h3 className="font-medium">Total Weight</h3>
                <p className="text-green-600 font-semibold">{results.totalWeight} {results.unit}</p>
              </div>
              <div>
                <h3 className="font-medium">Weight per Serving</h3>
                <p className="text-green-600 font-semibold">{results.weightPerServing} {results.unit}</p>
              </div>
            </div>
          </div>
        )}

        {results && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Serving Size Distribution</h2>
            <div className="h-64">
              <Pie data={pieData} options={pieOptions} />
            </div>
          </div>
        )}

        <div className="mt-16">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaBook className="mr-2" />
            How to Use
          </h2>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Enter the name of the food item</li>
            <li>Enter the total number of servings</li>
            <li>Enter the total weight of the food</li>
            <li>Select the unit of measurement (grams, ounces, milliliters, or cups)</li>
            <li>Click "Calculate Serving Size" to see the results</li>
            <li>View the pie chart to understand the serving size distribution</li>
          </ol>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaLightbulb className="mr-2" />
            Features & Benefits
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Accurate Measurements</h3>
              <p className="text-gray-600">
                Get precise serving size calculations for any food item
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Multiple Units</h3>
              <p className="text-gray-600">
                Choose from grams, ounces, milliliters, or cups for your measurements
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Visual Representation</h3>
              <p className="text-gray-600">
                View a pie chart showing the distribution of serving sizes
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Portion Control</h3>
              <p className="text-gray-600">
                Helps in maintaining healthy portion sizes for better nutrition
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaQuestionCircle className="mr-2" />
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">How do I measure food accurately?</h3>
              <p className="text-gray-600">
                Use a kitchen scale for best accuracy. For liquids, use measuring cups.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Why is portion control important?</h3>
              <p className="text-gray-600">
                Proper portion control helps maintain a healthy diet and manage weight.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">Can I use this for meal planning?</h3>
              <p className="text-gray-600">
                Yes! Use this calculator to plan balanced meals with appropriate portion sizes.
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-600 font-semibold mb-2">How do I convert between units?</h3>
              <p className="text-gray-600">
                The calculator supports multiple units. Choose the one that's most convenient for you.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodServingSizeCalculator;
